'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Shop from './Shop';

interface StartScreenProps {
  onStart: () => void;
}

export default function StartScreen({ onStart }: StartScreenProps) {
  const router = useRouter();
  const [visible, setVisible] = useState(false);
  const [blink, setBlink] = useState(true);
  const [highScore, setHighScore] = useState<number | null>(null);
  const [showShop, setShowShop] = useState(false);
  const [coins, setCoins] = useState(0);
  const [trophyCount, setTrophyCount] = useState(0);

  useEffect(() => {
    setVisible(true);
    const stored = localStorage.getItem('spaceracer-highscore');
    if (stored) setHighScore(parseInt(stored, 10));
    const storedCoins = parseInt(localStorage.getItem('spaceracer-coins') || '0', 10);
    setCoins(storedCoins);
    try {
      const trophies = JSON.parse(localStorage.getItem('spaceracer-trophies') || '[]');
      setTrophyCount(trophies.length);
    } catch { setTrophyCount(0); }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setBlink((b) => !b), 600);
    return () => clearInterval(interval);
  }, []);

  const handleOpenShop = () => {
    // Refresh coins when opening shop
    const storedCoins = parseInt(localStorage.getItem('spaceracer-coins') || '0', 10);
    setCoins(storedCoins);
    setShowShop(true);
  };

  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center"
      style={{
        background: 'radial-gradient(ellipse at center, rgba(0,30,60,0.6) 0%, rgba(2,4,10,0.3) 70%)',
      }}
    >
      {/* KORT button — top-right corner */}
      <button
        onClick={() => router.push('/galaxy-map')}
        className="absolute top-4 right-4 z-20 flex items-center gap-2 px-4 py-2 rounded font-mono-game text-xs tracking-[0.18em] uppercase transition-all hover:scale-105"
        style={{
          background: 'rgba(0,229,255,0.08)',
          border: '1px solid rgba(0,229,255,0.35)',
          color: '#00e5ff',
          textShadow: '0 0 8px rgba(0,229,255,0.6)',
          boxShadow: '0 0 16px rgba(0,229,255,0.08)',
        }}
      >
        <span style={{ fontSize: '1rem' }}>🌌</span>
        <span>KORT</span>
      </button>

      {/* Decorative rings */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div
          className="absolute rounded-full border"
          style={{
            width: '500px',
            height: '500px',
            borderColor: 'rgba(0,229,255,0.04)',
            animation: 'spin-slow 20s linear infinite',
          }}
        />
        <div
          className="absolute rounded-full border"
          style={{
            width: '700px',
            height: '700px',
            borderColor: 'rgba(0,229,255,0.02)',
            animation: 'spin-reverse 30s linear infinite',
          }}
        />
      </div>

      {/* Logo / title */}
      <div
        className="flex flex-col items-center gap-2 z-10"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(30px)',
          transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* Icon */}
        <div className="relative mb-4">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center"
            style={{
              background: 'radial-gradient(circle, rgba(0,100,200,0.3) 0%, rgba(0,50,100,0.1) 100%)',
              border: '1px solid rgba(0,229,255,0.3)',
              boxShadow: '0 0 30px rgba(0,229,255,0.2), 0 0 60px rgba(0,100,200,0.1)',
            }}
          >
            <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
              <path
                d="M22 4 L27 20 L36 20 L29 28 L32 40 L22 33 L12 40 L15 28 L8 20 L17 20 Z"
                fill="none"
                stroke="rgba(0,229,255,0.8)"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
              <path
                d="M22 8 L22 30 M18 18 L26 18"
                stroke="rgba(0,229,255,0.4)"
                strokeWidth="1"
              />
              <circle cx="22" cy="16" r="3" fill="rgba(0,229,255,0.6)" />
            </svg>
          </div>
          <div
            className="absolute -inset-4 rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(0,229,255,0.08) 0%, transparent 70%)',
              animation: 'pulse-glow 2s ease-in-out infinite',
            }}
          />
        </div>

        {/* Title */}
        <div className="text-center">
          <h1
            className="font-mono-game font-800 tracking-[0.15em] uppercase"
            style={{
              fontSize: 'clamp(2.5rem, 6vw, 5rem)',
              color: '#00e5ff',
              textShadow: '0 0 20px #00e5ff, 0 0 60px rgba(0,180,255,0.4)',
              animation: 'flicker 5s ease-in-out infinite',
            }}
          >
            SPACE
          </h1>
          <h1
            className="font-mono-game font-800 tracking-[0.3em] uppercase"
            style={{
              fontSize: 'clamp(2.5rem, 6vw, 5rem)',
              color: 'rgba(255,255,255,0.9)',
              textShadow: '0 0 10px rgba(255,255,255,0.3)',
              marginTop: '-8px',
            }}
          >
            RACER
          </h1>
        </div>

        <p
          className="font-mono-game text-sm tracking-[0.12em] mt-1"
          style={{ color: 'rgba(0, 229, 255, 0.5)' }}
        >
          UNDGÅ ASTEROIDERNE · OVERLEV SÅ LANGT DU KAN
        </p>

        {/* High score display */}
        {highScore !== null && (
          <div
            className="mt-4 px-6 py-2 rounded"
            style={{
              background: 'rgba(0, 229, 255, 0.05)',
              border: '1px solid rgba(0, 229, 255, 0.2)',
            }}
          >
            <span
              className="font-mono-game text-xs tracking-[0.15em] uppercase"
              style={{ color: 'rgba(0, 229, 255, 0.5)' }}
            >
              REKORD:{' '}
            </span>
            <span
              className="font-mono-game text-sm font-700"
              style={{ color: '#00e5ff', textShadow: '0 0 6px #00e5ff' }}
            >
              {highScore.toLocaleString('da-DK')}
            </span>
          </div>
        )}

        {/* Start CTA */}
        <button
          onClick={onStart}
          className="mt-8 relative group"
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <div
            className="px-12 py-3.5 rounded font-mono-game font-700 text-base tracking-[0.2em] uppercase transition-all duration-200"
            style={{
              background: blink
                ? 'rgba(0, 229, 255, 0.12)'
                : 'rgba(0, 229, 255, 0.06)',
              border: `1px solid ${blink ? 'rgba(0, 229, 255, 0.7)' : 'rgba(0, 229, 255, 0.3)'}`,
              color: blink ? '#00e5ff' : 'rgba(0, 229, 255, 0.6)',
              textShadow: blink ? '0 0 10px #00e5ff' : 'none',
              boxShadow: blink ? '0 0 20px rgba(0,229,255,0.15)' : 'none',
              transition: 'all 0.3s ease',
            }}
          >
            TRYK SPACE ELLER KLIK FOR AT STARTE
          </div>
        </button>

        {/* Shop button */}
        <button
          onClick={handleOpenShop}
          className="mt-3 relative group"
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <div
            className="flex items-center gap-3 px-8 py-3 rounded font-mono-game text-sm tracking-[0.18em] uppercase transition-all duration-200 hover:scale-105"
            style={{
              background: 'rgba(255,200,0,0.07)',
              border: '1px solid rgba(255,200,0,0.3)',
              color: '#ffd700',
              textShadow: '0 0 8px rgba(255,215,0,0.4)',
              boxShadow: '0 0 16px rgba(255,200,0,0.06)',
            }}
          >
            <span style={{ fontSize: '1.1rem' }}>🛒</span>
            <span>BUTIK</span>
            {coins > 0 && (
              <span
                className="flex items-center gap-1 text-xs"
                style={{ color: 'rgba(255,215,0,0.7)' }}
              >
                <span>🪙</span>
                <span>{coins.toLocaleString('da-DK')}</span>
              </span>
            )}
          </div>
        </button>

        {/* Trophy shelf button */}
        <button
          onClick={() => router.push('/trophy-shelf')}
          className="mt-2 relative group"
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <div
            className="flex items-center gap-3 px-8 py-3 rounded font-mono-game text-sm tracking-[0.18em] uppercase transition-all duration-200 hover:scale-105"
            style={{
              background: 'rgba(255,215,0,0.05)',
              border: '1px solid rgba(255,215,0,0.2)',
              color: 'rgba(255,215,0,0.8)',
              textShadow: '0 0 8px rgba(255,215,0,0.3)',
              boxShadow: '0 0 12px rgba(255,215,0,0.04)',
            }}
          >
            <span style={{ fontSize: '1.1rem' }}>🏆</span>
            <span>POKAL HYLDE</span>
            {trophyCount > 0 && (
              <span
                className="text-xs px-1.5 py-0.5 rounded"
                style={{
                  background: 'rgba(255,215,0,0.15)',
                  border: '1px solid rgba(255,215,0,0.3)',
                  color: 'rgba(255,215,0,0.9)',
                }}
              >
                {trophyCount}
              </span>
            )}
          </div>
        </button>

        {/* Controls */}
        <div
          className="mt-6 flex items-center gap-8"
          style={{ opacity: 0.5 }}
        >
          <div className="flex flex-col items-center gap-2">
            <div className="flex gap-1">
              <div
                className="w-8 h-8 rounded flex items-center justify-center font-mono-game text-xs"
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: 'rgba(255,255,255,0.7)',
                }}
              >
                A
              </div>
              <div
                className="w-8 h-8 rounded flex items-center justify-center font-mono-game text-xs"
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: 'rgba(255,255,255,0.7)',
                }}
              >
                D
              </div>
            </div>
            <span
              className="font-mono-game text-[10px] tracking-widest"
              style={{ color: 'rgba(255,255,255,0.4)' }}
            >
              ELLER
            </span>
            <div className="flex gap-1">
              <div
                className="w-8 h-8 rounded flex items-center justify-center font-mono-game text-xs"
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: 'rgba(255,255,255,0.7)',
                }}
              >
                ←
              </div>
              <div
                className="w-8 h-8 rounded flex items-center justify-center font-mono-game text-xs"
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: 'rgba(255,255,255,0.7)',
                }}
              >
                →
              </div>
            </div>
          </div>
          <div
            className="font-mono-game text-xs text-center"
            style={{ color: 'rgba(255,255,255,0.4)', maxWidth: '160px', lineHeight: '1.6' }}
          >
            STYR DIT RUMSKIB TIL VENSTRE OG HØJRE
          </div>
        </div>
      </div>

      {/* Shop overlay */}
      {showShop && <Shop onClose={() => setShowShop(false)} />}
    </div>
  );
}